import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { ContactInput, Contact } from '../types';
import type { TenantCustomField } from '../types';
import { apiService } from '../services/api';
import { validatePhone } from '../utils/phoneUtils';
import { ScrollableSelect } from './ScrollableSelect';

interface Category {
  id: string;
  nome: string;
}

const contactSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  telefone: z.string().min(1, 'Telefone é obrigatório').refine(validatePhone, {
    message: 'Formato de telefone inválido',
  }),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  observacoes: z.string().optional(),
  categoriaId: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

export type ContactFormVariant = 'modal' | 'page';

interface ContactFormProps {
  contact?: Contact;
  onSuccess: () => void;
  onCancel: () => void;
  /** 'modal' = overlay + box (default). 'page' = only form content, for use inside a page. */
  variant?: ContactFormVariant;
}

export function ContactForm({ contact, onSuccess, onCancel, variant = 'modal' }: ContactFormProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [customFields, setCustomFields] = useState<TenantCustomField[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, string | number | null>>({});

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      nome: contact?.nome || '',
      telefone: contact?.telefone || '',
      email: contact?.email || '',
      observacoes: contact?.observacoes || '',
      categoriaId: contact?.categoriaId || '',
    },
  });

  useEffect(() => {
    loadCategories();
    apiService.getCustomFields().then(setCustomFields).catch(() => setCustomFields([]));
  }, []);

  useEffect(() => {
    if (contact?.customData && typeof contact.customData === 'object') {
      setCustomValues(contact.customData as Record<string, string | number | null>);
    } else {
      setCustomValues({});
    }
  }, [contact?.id]);

  // Reset form when contact changes AND categories are loaded
  useEffect(() => {
    if (contact && !loadingCategories) {
      reset({
        nome: contact.nome || '',
        telefone: contact.telefone || '',
        email: contact.email || '',
        observacoes: contact.observacoes || '',
        categoriaId: contact.categoriaId || '',
      });
      if (contact.categoriaId) {
        setTimeout(() => setValue('categoriaId', contact.categoriaId), 100);
      }
    }
  }, [contact, categories, loadingCategories, reset, setValue]);

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/categorias', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.nome })),
    [categories]
  );

  const onSubmit = async (data: ContactFormData) => {
    try {
      const customData: Record<string, string | number | null> = {};
      for (const f of customFields) {
        const v = customValues[f.key];
        if (v !== undefined && v !== null && v !== '') customData[f.key] = v;
        else if (f.required) {
          toast.error(`Campo "${f.label}" é obrigatório`);
          return;
        }
      }
      const contactInput: ContactInput = {
        nome: data.nome,
        telefone: data.telefone,
        email: data.email || undefined,
        observacoes: data.observacoes || undefined,
        categoriaId: data.categoriaId || undefined,
        customData: Object.keys(customData).length ? customData : undefined,
      };

      if (contact) {
        await apiService.updateContact(contact.id, contactInput);
        toast.success('Contato atualizado com sucesso');
      } else {
        await apiService.createContact(contactInput);
        toast.success('Contato criado com sucesso');
      }

      onSuccess();
    } catch (err: any) {
      // Verificar se é erro de quota
      if (err?.isQuotaError || err?.upgradeRequired) {
        toast.error(err.message || 'Limite de contatos atingido. Faça upgrade do seu plano para continuar.', {
          duration: 6000,
          icon: '⚠️'
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Erro ao salvar contato';
      toast.error(errorMessage);
    }
  };

  const isPage = variant === 'page';
  const labelClass = isPage
    ? 'block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5'
    : 'block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2';
  const inputClass = isPage
    ? 'input-field w-full text-sm border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
    : 'input-field text-sm sm:text-base';

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} className={isPage ? 'space-y-5' : 'space-y-4 sm:space-y-5 md:space-y-6'}>
      <div className={isPage ? 'grid grid-cols-1 md:grid-cols-2 gap-5' : undefined}>
        <div>
          <label htmlFor="nome" className={labelClass}>
            Nome *
          </label>
          <input
            id="nome"
            type="text"
            {...register('nome')}
            className={inputClass}
            placeholder="Digite o nome completo"
          />
          {errors.nome && (
            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.nome.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="telefone" className={labelClass}>
            Telefone *
          </label>
          <input
            id="telefone"
            type="tel"
            {...register('telefone')}
            placeholder="+55 11 99999-9999"
            className={inputClass}
          />
          {errors.telefone && (
            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.telefone.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className={labelClass}>
            Email
          </label>
          <input
            id="email"
            type="email"
            {...register('email')}
            className={inputClass}
            placeholder="Digite o email"
          />
          {errors.email && (
            <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="categoriaId" className={labelClass}>
            Categoria
          </label>
          <Controller
            name="categoriaId"
            control={control}
            render={({ field }) => (
              <ScrollableSelect
                id="categoriaId"
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Selecione uma categoria"
                disabled={loadingCategories}
                loading={loadingCategories}
                options={categoryOptions}
              />
            )}
          />
        </div>
      </div>

      <div>
        <label htmlFor="observacoes" className={labelClass}>
          Observações
        </label>
        <textarea
          id="observacoes"
          {...register('observacoes')}
          rows={3}
          className={`${inputClass} resize-none`}
          placeholder="Digite observações adicionais"
        />
      </div>

      {customFields.length > 0 && (
        <div className={`space-y-4 border-t border-gray-200 dark:border-gray-700 ${isPage ? 'pt-5' : 'pt-4'}`}>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Campos personalizados
          </p>
          <div className={isPage ? 'grid grid-cols-1 md:grid-cols-2 gap-5' : 'space-y-4'}>
            {customFields.map((f) => (
              <div key={f.id}>
                <label htmlFor={`custom-${f.key}`} className={labelClass}>
                  {f.label} {f.required && '*'}
                </label>
                {f.type === 'date' ? (
                  <input
                    id={`custom-${f.key}`}
                    type="date"
                    value={customValues[f.key] != null ? String(customValues[f.key]) : ''}
                    onChange={(e) => setCustomValues((prev) => ({ ...prev, [f.key]: e.target.value || null }))}
                    className={inputClass}
                  />
                ) : f.type === 'number' ? (
                  <input
                    id={`custom-${f.key}`}
                    type="number"
                    value={customValues[f.key] != null ? customValues[f.key] : ''}
                    onChange={(e) => setCustomValues((prev) => ({ ...prev, [f.key]: e.target.value === '' ? null : Number(e.target.value) }))}
                    className={inputClass}
                  />
                ) : (
                  <input
                    id={`custom-${f.key}`}
                    type="text"
                    value={customValues[f.key] != null ? String(customValues[f.key]) : ''}
                    onChange={(e) => setCustomValues((prev) => ({ ...prev, [f.key]: e.target.value || null }))}
                    className={inputClass}
                    placeholder={f.required ? 'Obrigatório' : 'Opcional'}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

          {/* Mostrar ID do Perfex apenas se o contato foi importado do Perfex */}
          {contact?.perfexLeadId && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <label className="block text-xs sm:text-sm font-semibold text-purple-700 mb-1">
                ID do Lead no Perfex CRM
              </label>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-mono text-purple-800">{contact.perfexLeadId}</span>
              </div>
              <p className="text-xs text-purple-600 mt-1">
                Este contato foi importado do Perfex CRM
              </p>
            </div>
          )}

          <div className={`pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-3 ${variant !== 'page' ? 'sm:pt-6' : ''}`}>
            <button
              type="button"
              onClick={onCancel}
              className={
                variant === 'page'
                  ? 'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors'
                  : 'w-full sm:flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl font-medium transition-all duration-200 text-sm sm:text-base bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
              }
            >
              {variant === 'page' && <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />}
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={
                variant === 'page'
                  ? 'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-transparent text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  : 'w-full sm:flex-1 py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base font-medium btn-primary'
              }
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin text-base shrink-0" aria-hidden />
                  Salvando...
                </>
              ) : variant === 'page' ? (
                contact ? (
                  <>
                    <i className="fas fa-check text-base shrink-0" aria-hidden />
                    Salvar alterações
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus text-base shrink-0" aria-hidden />
                    Criar contato
                  </>
                )
              ) : (
                'Salvar Contato'
              )}
            </button>
          </div>
        </form>
  );

  if (variant === 'page') {
    return formContent;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-lg max-h-[95vh] overflow-y-auto border border-gray-100" role="dialog" aria-labelledby="form-title">
        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mx-auto mb-3 sm:mb-4 flex items-center justify-center">
            <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 id="form-title" className="text-xl sm:text-2xl font-bold text-gray-900">
            {contact ? 'Editar Contato' : 'Novo Contato'}
          </h2>
          <p className="text-sm sm:text-base text-gray-500 mt-1 sm:mt-2">
            {contact ? 'Atualize as informações do contato' : 'Preencha os dados para criar um novo contato'}
          </p>
        </div>
        {formContent}
      </div>
    </div>
  );
}